/**
 * Audio — public API for geometry-blast procedural synth audio.
 *
 * Usage:
 *   import { initAudio, playSound, setVolume, toggleMute } from '../audio';
 *   initAudio();           // Call after first user gesture
 *   playSound('explosion'); // Fire-and-forget
 */

import {
  initAudio as init,
  getAudioContext,
  getMasterGain,
  setVolume as setVol,
  getVolume as getVol,
  toggleMute as toggle,
  isMuted as muted,
} from './sound-manager';

import * as synth from './synth-sounds';
import { startMusic, stopMusic, onMusicStart, syncMusicVolume } from './music';

export type SoundKey =
  | 'explosion' | 'fire' | 'hit' | 'death' | 'bounce'
  | 'menu_select' | 'menu_nav' | 'turn_transition'
  | 'burst' | 'di_window' | 'edgeguard_beam'
  | 'grapple_launch' | 'wind_change' | 'announcement';

const SOUND_MAP: Record<SoundKey, (ctx: AudioContext, dest: AudioNode) => void> = {
  explosion: synth.explosion,
  fire: synth.fire,
  hit: synth.hit,
  death: synth.death,
  bounce: synth.bounce,
  menu_select: synth.menuSelect,
  menu_nav: synth.menuNav,
  turn_transition: synth.turnTransition,
  burst: synth.burst,
  di_window: synth.diWindow,
  edgeguard_beam: synth.edgeguardBeam,
  grapple_launch: synth.grappleLaunch,
  wind_change: synth.windChange,
  announcement: synth.announcement,
};

/** Initialize the audio system. Call once after first user gesture. */
export function initAudio(): void {
  init();
}

/** Play a named sound. No-op if audio not initialized or context suspended. */
export function playSound(key: SoundKey): void {
  const ctx = getAudioContext();
  const gain = getMasterGain();
  if (!ctx || !gain) return;
  // Resume suspended context (browsers auto-suspend until gesture)
  if (ctx.state === 'suspended') {
    ctx.resume();
  }
  const fn = SOUND_MAP[key];
  if (fn) fn(ctx, gain);
}

/** Set master volume (0–1). Persists to localStorage. */
export function setVolume(v: number): void {
  setVol(v);
  syncMusicVolume();
}

/** Get current master volume (0–1). */
export const getVolume = getVol;

/** Toggle mute on/off. Persists to localStorage. */
export function toggleMute(): void {
  toggle();
  syncMusicVolume();
}

/** Check if muted. */
export const isMuted = muted;

export { startMusic, stopMusic, onMusicStart };
